import { Box, Flex, Text } from '@chakra-ui/layout';
import { Tooltip } from '@chakra-ui/react';
import ActivityIndicator from 'components/ActivityIndicator';
import CardMy from 'components/CardMy';
import Title from 'components/CardMy/Title';
import Label from 'components/Label';
import MultipleSelect from 'components/MultipleSelect';
import { format, parse } from 'date-fns';
import { useState } from 'react';
import { MdInsertChart } from 'react-icons/md';
import { useAppSelector } from 'store/hooks';
import { selectAgentStatisticDetailed } from 'store/slices/agent';
import { ACTIVITIES } from 'types';
import DetailedPopup from './DetailedPopup';
import _ from 'lodash';
import { selectCallouts } from 'store/slices/companySetting';

const items = [
    { text: 'Calls', type: ACTIVITIES.calls },
    { text: 'Pickup', type: ACTIVITIES.pickUps },
    { text: 'Conversation', type: ACTIVITIES.convos },
    { text: 'SMS', type: ACTIVITIES.sms },
];

export const rowH = '24px';
interface Timeframe {
    time: string;
    [key: string]: any;
}

export const getFilters = (filterKeys: ACTIVITIES[]) => [
    ACTIVITIES.calls,
    ACTIVITIES.pickUps,
    ACTIVITIES.convos,
    ACTIVITIES.sms,
    ...filterKeys,
];

export function filterDataByKeys(
    data: { [key in ACTIVITIES]: any },
    filterKeys: ACTIVITIES[],
    isDetailed?: boolean,
): { name: ACTIVITIES; value: any }[] {
    const filters = getFilters(filterKeys);

    if (isDetailed) {
        return filters.map(key => ({
            name: key,
            value: data[key],
        }));
    }

    return filters.map(key => ({
        name: key,
        value: data[key],
    }));
}

const partsTime = (time: string) => parse(time, 'hh:mm:ss a', new Date()) as unknown as number;

const DetailedStatistics = () => {
    const data = useAppSelector(selectAgentStatisticDetailed);
    const callouts = useAppSelector(selectCallouts);

    const [isOpen, setIsOpen] = useState(false);
    const [time, setTime] = useState({});
    const [filters, setFilters] = useState([]);
    const filtersKeys = filters.map(item => item.value);

    if (_.isEmpty(data)) {
        return null;
    }

    return (
        <CardMy transparent>
            <Flex flexWrap="wrap" justifyContent="space-between" alignItems="center" w="100%" mb="20px">
                <Title icon={MdInsertChart}>Detailed Statistics</Title>
                <Flex gap="16px">
                    {items.map((item, index) => (
                        <Label text={item.text} type={item.type} key={index} />
                    ))}
                </Flex>
                <MultipleSelect value={filters} setValue={setFilters} />
            </Flex>
            <Flex gap="8px" flexDirection="column">
                {data.map(({ date, computed, timeframes }) => {
                    const day_info = filterDataByKeys(computed, filtersKeys);
                    const { workTime } = computed;

                    const parts = Object.entries(timeframes as object)
                        .map(([key, value]) => ({ time: key, ...value }))
                        .sort((a: Timeframe, b: Timeframe) => {
                            const timeA = partsTime(a.time);
                            const timeB = partsTime(b.time);
                            return timeA - timeB;
                        });

                    const parsedDate = parse(date, 'dd.MM.yyyy', new Date());

                    const formattedDate = format(parsedDate, 'd MMM');

                    return (
                        <Flex
                            key={date}
                            bg="white"
                            borderRadius="16px"
                            boxShadow="0px 4px 8px -2px rgba(16, 24, 40, 0.10), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)"
                        >
                            <Flex
                                flexDirection="column"
                                width="170px"
                                flexShrink={0}
                                borderRight="1px"
                                borderColor="gray.200"
                                gap="16px"
                                padding="24px"
                            >
                                <Text color="gray.900" fontWeight={700} fontSize="20px" letterSpacing="-0.6px" mb="6px">
                                    {formattedDate}
                                </Text>
                                {day_info.map(item => (
                                    <Text
                                        fontSize="14px"
                                        height={rowH}
                                        fontWeight="500"
                                        color="gray.500"
                                        key={item.name}
                                    >
                                        {callouts[item.name]}: &nbsp;
                                        <Text as="span" color="gray.900">
                                            {item.value}
                                        </Text>
                                    </Text>
                                ))}
                                <div>
                                    <Text fontSize="14px" fontWeight="500" color="gray.500">
                                        Total RGAT
                                    </Text>
                                    <Text color="gray.900" fontSize="16px" letterSpacing="-0.32px" fontWeight="700">
                                        {workTime}
                                    </Text>
                                </div>
                            </Flex>
                            <Box overflowX="auto" flexGrow={1}>
                                <Flex height="100%">
                                    {parts.map((hourData: any) => {
                                        const parsedTime = parse(hourData.time, 'hh:mm:ss a', new Date());
                                        const formattedTime = format(parsedTime, 'h a');
                                        const hour = filterDataByKeys(hourData, filtersKeys);
                                        const { efficiency } = hourData;

                                        const handleOpen = () => {
                                            setIsOpen(true);
                                            setTime({
                                                parsedDate: format(parsedDate, 'dd.MM.yyyy'),
                                                parsedTime: hourData.time,
                                            });
                                        };

                                        return (
                                            <Flex
                                                key={hourData.time}
                                                borderRight="1px"
                                                borderColor="gray.200"
                                                flex="1 0 63px"
                                                // flex="1 0 150px"
                                                height="100%"
                                                flexDirection="column"
                                                gap="16px"
                                                onClick={handleOpen}
                                                cursor="pointer"
                                                _hover={{
                                                    background: 'gray.50',
                                                }}
                                            >
                                                <Text
                                                    padding="8px"
                                                    fontSize="12px"
                                                    fontWeight="600"
                                                    color="gray.500"
                                                    mb="28px"
                                                >
                                                    {formattedTime}
                                                </Text>
                                                {hour.map(item => (
                                                    <Flex justifyContent="center" height={rowH} key={item.name}>
                                                        {item.value > 0 && (
                                                            <ActivityIndicator type={item.name} value={item.value} />
                                                        )}
                                                    </Flex>
                                                ))}
                                                <Tooltip hasArrow label={`Efficiency: ${efficiency}%`}>
                                                    <Box
                                                        mt="16px"
                                                        height="16px"
                                                        borderRadius="4px"
                                                        bg="green.400"
                                                        opacity={Number(efficiency) / 100}
                                                        margin="0 8px"
                                                    />
                                                </Tooltip>
                                            </Flex>
                                        );
                                    })}
                                </Flex>
                            </Box>
                        </Flex>
                    );
                })}
                {isOpen && (
                    <DetailedPopup isOpen={isOpen} setIsOpen={setIsOpen} time={time} filtersKeys={filtersKeys} />
                )}
            </Flex>
        </CardMy>
    );
};

export default DetailedStatistics;
